import React, { useState } from 'react';
import { RosterVersion, rollbackVersion, deleteVersion, updateRosterVersionNotes } from '../../services/rosterService';
import { format } from 'date-fns';
import { getRosterVersionLabel } from '../../utils/rosterVersionLabel';

interface VersionControlPanelProps {
    versions: RosterVersion[];
    currentVersion: RosterVersion | null;
    onVersionChange: (version: RosterVersion | null) => void;
    onPublish: () => void;
    onUnpublish: () => void;
    onRefresh?: () => void;
}

const VersionControlPanel: React.FC<VersionControlPanelProps> = ({
    versions,
    currentVersion,
    onVersionChange,
    onPublish,
    onUnpublish,
    onRefresh,
}) => {
    const [showRollbackConfirm, setShowRollbackConfirm] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);
    const [notesVersion, setNotesVersion] = useState<RosterVersion | null>(null);
    const [notesDraft, setNotesDraft] = useState('');
    const [notesError, setNotesError] = useState('');
    const hasMeaningfulNotes = (notes?: string | null) => {
        const trimmed = notes?.trim() || '';
        return trimmed.length > 0 && !/^auto-generated via intelligent roster wizard$/i.test(trimmed);
    };
    const versionLabel = (version: RosterVersion) => getRosterVersionLabel(version, versions);

    const openNotesEditor = (version: RosterVersion) => {
        setNotesVersion(version);
        setNotesDraft(version.notes || '');
        setNotesError('');
    };

    const handleRollback = async (versionId: string) => {
        setProcessing(true);
        try {
            await rollbackVersion(versionId);
            setShowRollbackConfirm(null);
            onRefresh?.();
        } catch (error) {
            console.error('Failed to rollback', error);
        } finally {
            setProcessing(false);
        }
    };

    const handleDelete = async (versionId: string) => {
        setProcessing(true);
        try {
            await deleteVersion(versionId);
            setShowDeleteConfirm(null);
            onRefresh?.();
        } catch (error) {
            console.error('Failed to delete', error);
        } finally {
            setProcessing(false);
        }
    };

    const handleSaveNotes = async () => {
        if (!notesVersion || processing) return;
        if (notesDraft.trim().length > 5000) {
            setNotesError('Notes must be 5000 characters or fewer.');
            return;
        }

        setProcessing(true);
        setNotesError('');
        try {
            const updated = await updateRosterVersionNotes(notesVersion.id, notesDraft);
            setNotesVersion(null);
            onVersionChange(updated);
            onRefresh?.();
        } catch (error: any) {
            setNotesError(error.response?.data?.message || 'Failed to save roster notes.');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <h3 className="font-bold text-gray-800 mb-3 border-b pb-2">Version Control</h3>

            {currentVersion && (
                <div className="mb-3 p-2 bg-blue-50 rounded">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-sm font-semibold text-blue-800">
                                {versionLabel(currentVersion)}
                            </div>
                            <div className="text-xs text-blue-600">
                                {currentVersion.status === 'PUBLISHED'
                                    ? 'Published'
                                    : currentVersion.status === 'DRAFT'
                                        ? 'Draft'
                                        : 'Archived'}
                                {currentVersion.publishedAt && (
                                    <span> • {format(new Date(currentVersion.publishedAt), 'MMM d, yyyy')}</span>
                                )}
                            </div>
                            <div className="text-xs text-blue-600">
                                {currentVersion.shiftCount} shifts
                            </div>
                        </div>
                        <div className="flex shrink-0 flex-col gap-1.5">
                            <button
                                onClick={() => openNotesEditor(currentVersion)}
                                disabled={processing}
                                className="px-2.5 py-1.5 rounded-md bg-white text-blue-700 border border-blue-200 text-xs font-semibold hover:bg-blue-50 disabled:opacity-50"
                            >
                                Notes
                            </button>
                            {currentVersion.status === 'PUBLISHED' ? (
                                <button
                                    onClick={onUnpublish}
                                    disabled={processing}
                                    className="px-2.5 py-1.5 rounded-md bg-amber-100 text-amber-800 text-xs font-semibold hover:bg-amber-200 disabled:opacity-50"
                                >
                                    Move to Draft
                                </button>
                            ) : currentVersion.status === 'DRAFT' ? (
                                <button
                                    onClick={onPublish}
                                    disabled={processing}
                                    className="px-2.5 py-1.5 rounded-md bg-emerald-100 text-emerald-800 text-xs font-semibold hover:bg-emerald-200 disabled:opacity-50"
                                >
                                    Publish
                                </button>
                            ) : null}
                        </div>
                    </div>
                    {hasMeaningfulNotes(currentVersion.notes) && (
                        <div className="mt-2 max-h-12 overflow-hidden rounded-md border border-blue-100 bg-white/70 px-2 py-1.5 text-xs text-blue-700">
                            {currentVersion.notes}
                        </div>
                    )}
                </div>
            )}

            <div className="max-h-48 overflow-y-auto space-y-2">
                {versions.length === 0 ? (
                    <div className="text-sm text-gray-500 p-2 text-center">
                        No roster version for this month. Use Create New Roster to begin this calendar month.
                    </div>
                ) : (
                    versions.map((version) => (
                        <div
                            key={version.id}
                            className={`p-2 rounded text-sm cursor-pointer transition-colors ${currentVersion?.id === version.id
                                ? 'bg-blue-100 border border-blue-300'
                                : 'hover:bg-gray-50 border border-gray-200'
                                }`}
                            onClick={() => onVersionChange(version)}
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="font-semibold">{versionLabel(version)}</div>
                                    <div className="text-xs text-gray-500">
                                        {format(new Date(version.startDate), 'MMM d')} -{' '}
                                        {format(new Date(version.endDate), 'MMM d')}
                                    </div>
                                    <div className="text-xs">
                                        <span
                                            className={`px-1 rounded ${version.status === 'PUBLISHED'
                                                ? 'bg-green-100 text-green-700'
                                                : version.status === 'ARCHIVED'
                                                    ? 'bg-gray-100 text-gray-700'
                                                    : 'bg-yellow-100 text-yellow-700'
                                                }`}
                                        >
                                            {version.status}
                                        </span>
                                        <span className="ml-2 text-gray-500">
                                            {version.shiftCount} shifts
                                        </span>
                                        {hasMeaningfulNotes(version.notes) && (
                                            <span className="ml-2 text-blue-600 font-medium">
                                                Notes
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                    <button
                                        onClick={() => openNotesEditor(version)}
                                        className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                                        disabled={processing}
                                    >
                                        Notes
                                    </button>
                                    {version.status === 'PUBLISHED' && version.id !== currentVersion?.id && (
                                        <button
                                            onClick={() => setShowRollbackConfirm(version.id)}
                                            className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                                            disabled={processing}
                                        >
                                            Rollback
                                        </button>
                                    )}
                                    {version.status === 'DRAFT' && (
                                        <button
                                            onClick={() => setShowDeleteConfirm(version.id)}
                                            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                                            disabled={processing}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {showRollbackConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md">
                        <h3 className="text-lg font-bold mb-4">Confirm Rollback</h3>
                        <p className="mb-4 text-gray-600">
                            Are you sure you want to rollback to this version? This will create a new
                            draft version based on the selected version.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowRollbackConfirm(null)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                                disabled={processing}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleRollback(showRollbackConfirm)}
                                className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                                disabled={processing}
                            >
                                {processing ? 'Processing...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md">
                        <h3 className="text-lg font-bold mb-4 text-red-600">Confirm Deletion</h3>
                        <p className="mb-4 text-gray-600">
                            Are you sure you want to delete this version? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                                disabled={processing}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(showDeleteConfirm)}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                disabled={processing}
                            >
                                {processing ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {notesVersion && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
                    <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-lg">
                        <div className="mb-4">
                            <h3 className="text-lg font-bold text-gray-900">Roster Notes</h3>
                            <p className="text-sm text-gray-500">
                                {versionLabel(notesVersion)} · {format(new Date(notesVersion.startDate), 'MMM d')} - {format(new Date(notesVersion.endDate), 'MMM d')}
                            </p>
                        </div>
                        <textarea
                            value={notesDraft}
                            onChange={(event) => setNotesDraft(event.target.value)}
                            className="w-full min-h-40 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            placeholder="Add admin notes, availability reminders, agency meetings, busy days, or cover risks..."
                            maxLength={5000}
                        />
                        <div className="mt-2 flex items-center justify-between text-xs">
                            <span className={notesError ? 'text-red-600' : 'text-gray-400'}>{notesError || 'Visible on the admin roster as a collapsible note.'}</span>
                            <span className="text-gray-400">{notesDraft.length}/5000</span>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                onClick={() => setNotesVersion(null)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                                disabled={processing}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveNotes}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                disabled={processing}
                            >
                                {processing ? 'Saving...' : 'Save Notes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VersionControlPanel;
